import { useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import {
  AlertCircle,
  Beaker,
  BookOpen,
  FileText,
  Heart,
  Info,
  TestTube2,
  Wrench,
  X,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  getCollectionLabel,
  getRelatedBlocks,
  type ContentBlock,
  type TestBody,
} from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { IconButton, SectionTitle } from "@louise/moon-ui";
import FactSheetHeader from "../../components/FactSheetHeader";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import { getTestIllustration } from "../../components/access/testIllustrations";
import { EMBER } from "../../../onboarding/theme";
import orchidLogo from "../../../assets/images/testing-logos/orchid.png";
import herasightLogo from "../../../assets/images/testing-logos/herasight.png";

type Props = NativeStackScreenProps<AccessStackParamList, "PgtComparison">;
type PgtTab = "overview" | "values" | "biology" | "testing";
type ResultType = NonNullable<TestBody["resultTypes"]>[number];

const PGT_PARENT_ID = "test-preimplantation-genetic-testing";
const PGT_TYPE_IDS = new Set([
  PGT_PARENT_ID,
  "test-pgt-a",
  "test-pgt-m",
  "test-pgt-sr",
  "test-pgt-p",
]);

const PGT_COMMERCIAL_LOGOS: Record<string, typeof orchidLogo> = {
  "test-pgt-a-products-orchid": orchidLogo,
  "test-pgt-a-products-herasight": herasightLogo,
};

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidates = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    getBlocksByKind("test").find((block) =>
      candidates.includes(String(block.id)),
    ) ?? null
  );
}

function getPgtTypes(test: ContentBlock<"test">): ContentBlock<"test">[] {
  const parentId = test.body.parentTest?.testId ?? test.id;
  const parent = getTestBlock(String(parentId));
  if (!parent) return [];

  return getRelatedBlocks(parent.id, "related-to")
    .filter(
      ({ relationship, block }) =>
        relationship.from === parent.id &&
        relationship.label === "Subtype" &&
        block.kind === "test" &&
        PGT_TYPE_IDS.has(String(block.id)),
    )
    .map(({ block }) => block)
    .filter((block): block is ContentBlock<"test"> => block.kind === "test");
}

function getPgtCommercialProfiles(
  test: ContentBlock<"test">,
): ContentBlock<"test">[] {
  return getRelatedBlocks(test.id, "related-to")
    .filter(
      ({ relationship, block }) =>
        relationship.from === test.id &&
        relationship.label === "Sold as" &&
        block.kind === "test",
    )
    .map(({ block }) => block)
    .filter((block): block is ContentBlock<"test"> => block.kind === "test")
    .filter((block) => block.body.isCommercialProductProfile);
}

function BodyCopy({ children }: { children: string }) {
  return (
    <Text
      className="font-dm-sans"
      style={{ fontSize: 14, lineHeight: 22, color: EMBER.colors.textLight }}
    >
      {children}
    </Text>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 8 }}>
      {items.map((item) => (
        <Text
          key={item}
          className="font-dm-sans"
          style={{
            fontSize: 13,
            lineHeight: 20,
            color: EMBER.colors.textLight,
          }}
        >
          • {item}
        </Text>
      ))}
    </View>
  );
}

function QuickFacts({ items }: { items: NonNullable<TestBody["quickFacts"]> }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      {items.map((item, index) => (
        <View
          key={`${item.kind}-${index}`}
          style={{
            width: "48%",
            minHeight: 68,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
            paddingHorizontal: 12,
            paddingVertical: 10,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              fontWeight: "700",
              color: EMBER.colors.textFaint,
              textTransform: "uppercase",
            }}
          >
            {item.label ?? item.kind}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              marginTop: 5,
              fontSize: 13,
              lineHeight: 18,
              fontWeight: "600",
              color: EMBER.colors.textMid,
            }}
          >
            {item.value}
          </Text>
        </View>
      ))}
    </View>
  );
}

function ResultTypeList({ items }: { items: ResultType[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item) => (
        <View
          key={item.label}
          style={{
            borderRadius: 12,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
            paddingHorizontal: 14,
            paddingVertical: 12,
            gap: 5,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              lineHeight: 18,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {item.label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {item.meaning}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textMid,
            }}
          >
            {item.whatToExpect}
          </Text>
        </View>
      ))}
    </View>
  );
}

function PgtOptionCard({
  test,
  onPress,
}: {
  test: ContentBlock<"test">;
  onPress: () => void;
}) {
  const cost = test.body.costRange
    ? `$${test.body.costRange.low}–$${test.body.costRange.high}`
    : undefined;
  const logo = PGT_COMMERCIAL_LOGOS[String(test.id)];
  const imageSource =
    logo ??
    getTestIllustration({
      testId: String(test.id),
      category: test.body.category,
    });

  return (
    <View style={{ width: "48%" }}>
      <AccessGridCard
        title={test.title}
        description={test.subtitle}
        imageSource={imageSource}
        imageStyle={
          logo ? { width: 128, height: 40 } : { width: 66, height: 84 }
        }
        onPress={onPress}
        tags={
          <>
            <AccessGridCardPill label={getCollectionLabel(test.body)} />
            {test.body.turnaroundTime ? (
              <AccessGridCardPill label={test.body.turnaroundTime} />
            ) : null}
            {cost ? <AccessGridCardPill label={cost} /> : null}
          </>
        }
      />
    </View>
  );
}

export default function PgtComparisonScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState<PgtTab>("overview");
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);
  const handleBack = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const test = useMemo(() => getTestBlock(route.params.testId), [route.params]);
  const testingOptions = useMemo(() => {
    if (!test) return [];
    return String(test.id) === PGT_PARENT_ID
      ? getPgtTypes(test)
      : getPgtCommercialProfiles(test);
  }, [test]);

  if (!test) return null;

  const body = test.body;
  const tabs: FactSheetTab<PgtTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "values",
      label: t("care.lab_result.tab_values"),
      shortLabel: t("care.lab_result.tab_values_short"),
      icon: FileText,
    },
    {
      value: "biology",
      label: t("care.lab_result.tab_biology"),
      shortLabel: t("care.lab_result.tab_biology_short"),
      icon: Beaker,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  const howItWorksSteps =
    body.howItWorksSteps ??
    body.howItWorks
      .split(/(?<=[.!?])\s+/)
      .map((step) => step.trim())
      .filter(Boolean);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            ...GLASS_CARD_STYLE,
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 32,
          }}
        >
          <View style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
            <IconButton
              onPress={handleBack}
              hitSlop={8}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.05)"
              pressedBackgroundColor="rgba(0,0,0,0.05)"
              pressedOpacity={1}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            >
              <X size={16} color={EMBER.colors.textLight} />
            </IconButton>
          </View>

          <FactSheetHeader
            title={test.title}
            subtitle={test.subtitle}
            imageSource={getTestIllustration({
              testId: String(test.id),
              category: body.category,
            })}
            imageSize={86}
            badges={[{ label: t("learn.test_detail.category_genetic") }]}
          />

          <FactSheetTabControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Heart}>
                  {t("learn.test_detail.section_what_it_measures")}
                </SectionTitle>
                <BodyCopy>{body.whatItMeasures}</BodyCopy>
              </View>

              {body.quickFacts?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Info}>
                    {t("access.testing.comparison.at_a_glance")}
                  </SectionTitle>
                  <QuickFacts items={body.quickFacts} />
                </View>
              ) : null}

              {body.keyFindings?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_why_it_matters")}
                  </SectionTitle>
                  <BulletList
                    items={body.keyFindings.map((finding) => finding.outcome)}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "values" ? (
            <View style={{ marginTop: 24, gap: 20 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={FileText}>
                  {t("learn.test_detail.section_possible_results")}
                </SectionTitle>
                {body.resultTypes?.length ? (
                  <ResultTypeList items={body.resultTypes} />
                ) : (
                  <BodyCopy>
                    {t("learn.test_detail.no_result_types_available")}
                  </BodyCopy>
                )}
              </View>
              {body.considerations?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_considerations")}
                  </SectionTitle>
                  <BulletList items={body.considerations} />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "biology" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitle icon={Wrench}>
                  {t("learn.test_detail.section_how_it_works")}
                </SectionTitle>
                <BodyCopy>{body.howItWorks}</BodyCopy>
                <BulletList items={howItWorksSteps} />
              </View>
              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 16 }}>
              <BodyCopy>
                {String(test.id) === PGT_PARENT_ID
                  ? t("access.testing.comparison.pgt_types_description")
                  : t(
                      "access.testing.comparison.pgt_commercial_options_description",
                    )}
              </BodyCopy>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {testingOptions.map((pgtType) => (
                  <PgtOptionCard
                    key={pgtType.id}
                    test={pgtType}
                    onPress={() =>
                      navigation.navigate("TestDetail", {
                        testId: String(pgtType.id),
                        returnTab: route.params.returnTab,
                      })
                    }
                  />
                ))}
              </View>
              {testingOptions.length === 0 ? (
                <BodyCopy>
                  {t("access.testing.comparison.pgt_commercial_options_empty")}
                </BodyCopy>
              ) : null}
            </View>
          ) : null}

          <View
            style={{
              marginTop: 32,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: EMBER.colors.textFaint,
                lineHeight: 17,
                textAlign: "center",
              }}
            >
              {t("learn.disclaimer_medical_info")}
            </Text>
          </View>
        </View>
      </ScrollView>
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
